import React, { useEffect, useState } from 'react';
import { Download, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';
import { personalInfo, floatingTags } from '../data/mock';
import ThreeParticles from './ThreeParticles';

const Hero = () => {
  const [mounted, setMounted] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [tagPositions, setTagPositions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [highlightedTag, setHighlightedTag] = useState(0);

  // Cycle through tags for highlight animation
  useEffect(() => {
    const interval = setInterval(() => {
      setHighlightedTag((prev) => (prev + 1) % floatingTags.length);
    }, 1000); // Change highlighted tag every 1 second
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Detect mobile for performance optimization
    const checkMobile = () => window.innerWidth < 768;
    setIsMobile(checkMobile());

    // Set mounted immediately to prevent render delays
    setTimeout(() => setMounted(true), 10);

    // Generate random positions for each tag - avoid center text area
    const positions = floatingTags.map(() => {
      let x, y;
      do {
        x = Math.random() * 90 + 5; // 5-95% of screen width
        y = Math.random() * 90 + 5; // 5-95% of screen height
      } while (
        // Avoid center area where text is (25-75% width, 20-80% height)
        x > 25 && x < 75 && y > 20 && y < 80
      );

      return {
        x: x,
        y: y,
        baseX: x,
        baseY: y
      };
    });
    setTagPositions(positions);

    // Update mobile state on resize
    const handleResize = () => setIsMobile(checkMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track mouse position
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen flex flex-col justify-center items-center relative px-6 pt-20 pb-16 bg-white dark:bg-gray-950 overflow-hidden transition-colors duration-500">
      {/* Cursor Interactive Particles - Disable on mobile for performance */}
      {!isMobile && (
        <div className="absolute inset-0 overflow-hidden">
          <ThreeParticles />
        </div>
      )}

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Particles - Reduced count on mobile */}
        {[...Array(isMobile ? 10 : 25)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="absolute w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          />
        ))}


        {/* Moving Gradient Orbs - Simplified on mobile */}
        {!isMobile && (
          <>
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gray-100 dark:bg-gray-800 rounded-full blur-3xl animate-morph opacity-40 dark:opacity-20" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gray-200 dark:bg-gray-700 rounded-full blur-3xl animate-morph-reverse opacity-30 dark:opacity-15" />
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gray-100 dark:bg-gray-800 rounded-full blur-3xl animate-float opacity-20 dark:opacity-10" />
          </>
        )}
      </div>

      {/* Floating Tags - Interactive Mesh */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingTags.map((tag, index) => {
          if (tagPositions.length === 0) return null;

          const pos = tagPositions[index];

          // Disable mouse interaction on mobile for performance
          let offsetX = 0;
          let offsetY = 0;
          if (!isMobile) {
            const distance = Math.sqrt(
              Math.pow(mousePos.x - pos.baseX, 2) + Math.pow(mousePos.y - pos.baseY, 2)
            );
            const isNear = distance < 20; // Threshold for interaction

            // Calculate displacement when mouse is near - move faster
            if (isNear) {
              const angle = Math.atan2(pos.baseY - mousePos.y, pos.baseX - mousePos.x);
              const pushDistance = (20 - distance) * 2.5; // Increased from 0.8 to 2.5
              offsetX = Math.cos(angle) * pushDistance;
              offsetY = Math.sin(angle) * pushDistance;
            }
          }

          const isHighlighted = index === highlightedTag;

          return (
            <span
              key={tag}
              className={`absolute text-xs md:text-sm font-light transition-all duration-700 ${mounted ? '' : 'opacity-0'
                } text-gray-500 dark:text-gray-500`}
              style={{
                top: `${pos.baseY + offsetY}%`,
                left: `${pos.baseX + offsetX}%`,
                transform: `translate(-50%, -50%) ${mounted ? 'scale(1)' : 'scale(0)'}`,
                transitionDelay: mounted ? '0ms' : `${index * 50}ms`,
                opacity: mounted ? (isHighlighted ? 0.99 : 0.30) : 0
              }}
            >
              {tag}
            </span>
          );
        })}
      </div>



      {/* Main Content */}
      <div className="max-w-4xl mx-auto text-center relative z-10">
        {/* Name - Single line with smaller font */}
        <h1
          className={`text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight text-black dark:text-white mb-6 whitespace-nowrap transition-all ${isMobile ? 'opacity-100 translate-y-0' : `duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }`}
        >
          {personalInfo.name.split('').map((letter, i) => (
            <span
              key={i}
              className="inline-block hover:scale-110 hover:-translate-y-1 transition-transform duration-200"
              style={{ animationDelay: `${i * 30}ms` }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </span>
          ))}
        </h1>

        {/* Headline */}
        <p
          className={`text-lg md:text-xl lg:text-2xl font-light text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8 leading-relaxed transition-all ${isMobile ? 'opacity-100 translate-y-0' : `duration-700 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }`}
        >
          {personalInfo.headline}
        </p>

        {/* Bio */}
        <div
          className={`text-[15px] md:text-[17px] font-light text-gray-500 dark:text-gray-500 max-w-6xl mx-auto mb-12 leading-relaxed transition-all ${isMobile ? 'opacity-100 translate-y-0' : `duration-700 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }`}
        >
          <p className="mb-2">I design and ship reliable backend systems, event-driven architectures, and intelligent full-stack applications.</p>
          <p>I enjoy solving complex technical problems, working with distributed systems, and turning ambitious product ideas into production-ready software.</p>
        </div>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all ${isMobile ? 'opacity-100 translate-y-0' : `duration-700 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
            }`}
        >
          <Button
            className="bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 rounded-full px-8 py-6 text-base font-light transition-all duration-300 hover:scale-105 hover:shadow-lg btn-glow"
            onClick={() => {
              window.open(personalInfo.resumeUrl, '_blank');
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            Download Resume
          </Button>
          <Button
            variant="outline"
            className="border-black dark:border-white text-black dark:text-white hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black rounded-full px-8 py-6 text-base font-light transition-all duration-300 hover:scale-105"
            onClick={() => scrollToSection('#projects')}
          >
            My Recent Work
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all ${isMobile ? 'opacity-100' : `duration-700 delay-500 ${mounted ? 'opacity-100' : 'opacity-0'}`
          }`}
      >
        <button
          onClick={() => scrollToSection('#about')}
          className="text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white transition-colors animate-bounce"
        >
          <ArrowDown size={24} />
        </button>
      </div>
    </section>
  );
};

export default Hero;
